// tapClassifier — state-machine för single/double/triple-tap-detection.
//
// Funktionsspec:
//   - SINGLE-tap firar 300ms efter senaste tap om inga fler kommer
//     (debounce-väntan för att rule:a ut double/triple).
//   - DOUBLE-tap firar omedelbart vid tap 2 om < 300ms sedan tap 1.
//     Men: vi vill kunna ha både double (fit-to-screen) OCH triple
//     (open sheet list) → double-tap fyrar EFTER 300ms från tap 2,
//     dvs inte omedelbart, för att rule:a ut tap 3. Detta ger en
//     ~300ms percieved delay för fit-to-screen — standard UX-tradeoff.
//   - TRIPLE-tap firar omedelbart vid tap 3 om totalt fönster ≤ 600ms.
//
// Position-tolerans: tap 2 och 3 måste vara inom 30px från tap 1
// (annars är det två separata taps på olika ställen).

import type { Point } from './quadrant';

export type TapKind = 'single' | 'double' | 'triple';

const SINGLE_DELAY_MS = 300;
const DOUBLE_DELAY_MS = 300;
const TRIPLE_TOTAL_MS = 600;
const TAP_POSITION_TOLERANCE = 30;

export interface TapClassifierCallbacks {
  onSingle: (point: Point) => void;
  onDouble: (point: Point) => void;
  onTriple: (point: Point) => void;
}

export interface TapClassifier {
  /** Anropas vid touchend när gesten klassats som "tap" (kort, ingen rörelse). */
  registerTap: (point: Point, now?: number) => void;
  /** Cancel alla väntande timers — kalla vid unmount eller mode-byte. */
  cancel: () => void;
}

interface PendingTap {
  point: Point;
  firstAt: number;
  count: 1 | 2;
  timerId: ReturnType<typeof setTimeout>;
}

export function createTapClassifier(cb: TapClassifierCallbacks): TapClassifier {
  let pending: PendingTap | null = null;

  const distance = (a: Point, b: Point) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  const clearPending = () => {
    if (pending) {
      clearTimeout(pending.timerId);
      pending = null;
    }
  };

  const registerTap = (point: Point, now = Date.now()) => {
    if (!pending) {
      // Första tap → vänta 300ms för att se om en till kommer
      const timerId = setTimeout(() => {
        if (pending && pending.count === 1) {
          const p = pending.point;
          pending = null;
          cb.onSingle(p);
        }
      }, SINGLE_DELAY_MS);
      pending = { point, firstAt: now, count: 1, timerId };
      return;
    }

    // Position-check: är det här samma plats?
    if (distance(point, pending.point) > TAP_POSITION_TOLERANCE) {
      // Annan plats → fire första tap som single och börja om
      clearPending();
      const timerId = setTimeout(() => {
        if (pending && pending.count === 1) {
          const p = pending.point;
          pending = null;
          cb.onSingle(p);
        }
      }, SINGLE_DELAY_MS);
      pending = { point, firstAt: now, count: 1, timerId };
      cb.onSingle(pending.point);
      return;
    }

    if (pending.count === 1) {
      // Andra tap → vänta 300ms för triple-möjlighet, men endast
      // upp till TRIPLE_TOTAL_MS från first.
      clearTimeout(pending.timerId);
      const remaining = TRIPLE_TOTAL_MS - (now - pending.firstAt);
      const wait = Math.max(0, Math.min(DOUBLE_DELAY_MS, remaining));
      const timerId = setTimeout(() => {
        if (pending && pending.count === 2) {
          const p = pending.point;
          pending = null;
          cb.onDouble(p);
        }
      }, wait);
      pending = { point, firstAt: pending.firstAt, count: 2, timerId };
      return;
    }

    // count === 2 → tap 3
    const elapsed = now - pending.firstAt;
    clearPending();
    if (elapsed <= TRIPLE_TOTAL_MS) {
      cb.onTriple(point);
    } else {
      // För sent — räkna som en ny första tap
      const timerId = setTimeout(() => {
        if (pending && pending.count === 1) {
          const p = pending.point;
          pending = null;
          cb.onSingle(p);
        }
      }, SINGLE_DELAY_MS);
      pending = { point, firstAt: now, count: 1, timerId };
    }
  };

  return { registerTap, cancel: clearPending };
}

export const TAP_CLASSIFIER_CONFIG = {
  SINGLE_DELAY_MS,
  DOUBLE_DELAY_MS,
  TRIPLE_TOTAL_MS,
  TAP_POSITION_TOLERANCE,
} as const;
